import React, { useEffect } from 'react';
import { useAsync } from 'react-use';

import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { NotFoundError } from '@backstage/errors';
import {
  catalogApiRef,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

export const UserGroupsPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    schema: { title = 'Owner', description = '' },
    required,
    disabled,
    rawErrors,
    formData,
    idSchema,
    uiSchema,
  } = props;

  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);

  const groupKind: string =
    (uiSchema?.['ui:options'] as any)?.groupKind ?? 'supervisory-org';

  const entityRefDefaults = {
    defaultKind: 'Group',
    defaultNamespace: 'default',
  };

  const { value: groups, loading } = useAsync(async () => {
    const { userEntityRef } = await identityApi.getBackstageIdentity();

    if (!userEntityRef) {
      errorApi.post(new NotFoundError('No user entity ref found'));
      return [];
    }

    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'Group',
        'relations.hasMember': userEntityRef,
        'spec.type': groupKind,
      },
      fields: ['metadata.name', 'metadata.namespace', 'metadata.title', 'kind'],
    });

    return items;
  });

  useEffect(() => {
    if (groups && groups.length > 0 && !formData) {
      onChange(stringifyEntityRef(groups[0]));
    }
  }, [groups, formData, onChange]);

  const selectedGroup =
    groups?.find(g => stringifyEntityRef(g) === formData) ?? null;

  const hasError = rawErrors?.length > 0;

  return (
    <FormControl fullWidth required={required} error={hasError && !formData}>
      <Autocomplete
        id={idSchema?.$id}
        value={selectedGroup}
        loading={loading}
        disabled={disabled}
        options={groups ?? []}
        getOptionLabel={option =>
          option.metadata.title ??
          humanizeEntityRef(option, entityRefDefaults) ??
          option.metadata.name
        }
        getOptionSelected={(option, value) =>
          stringifyEntityRef(option) === stringifyEntityRef(value)
        }
        onChange={(_, value) => {
          onChange(value ? stringifyEntityRef(value) : undefined);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            size="small"
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
            error={hasError && !formData}
          />
        )}
        loadingText="Loading groups..."
        noOptionsText="No groups found. You may not be a member of any supervisory org group."
      />
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};
